import { manufacturingFetch } from "@/lib/manufacturingClient";
import type { BuilderAssistantSession } from "@/lib/manufacturing/builderAssistantTypes";

const PATH = "/api/mfg/builder-assistant";

function post<T>(body: Record<string, unknown>) {
  return manufacturingFetch<T>(PATH, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export type QcAwaitingRow = {
  key: string;
  serial: string;
  model: string;
  size: string;
  stage: string;
  session_id: string | null;
  unit_id: string | null;
};

export type QcFailureRow = {
  id: string;
  session_id: string;
  serial: string;
  group: string;
  item: string;
  model: string;
  size: string;
  inspector: string;
  observed_at: string;
  open: boolean;
  severity: string;
  photos: string[];
};

export type QcOpenReport = {
  session_id: string;
  serial: string;
  model: string;
  size: string;
  open_count: number;
  completed_at: string | null;
  status: string;
};

export type QcDashboard = {
  awaiting: QcAwaitingRow[];
  failures: QcFailureRow[];
  failure_total: number;
  failure_page: number;
  failure_page_size: number;
  open_reports: QcOpenReport[];
  open_report_count: number;
  open_defect_count: number;
  trends: {
    last_7_days: number;
    prior_7_days: number;
    per_day: { day: string; count: number }[];
    failures_per_unit_7d: number | null;
  };
  leader_stamps: { at: string; group: string; item: string }[];
};

export type QcHistoryStatus = "uncorrected" | "open" | "passed" | "final" | "all";

export type QcHistorySession = BuilderAssistantSession & {
  open_failures: number;
  actor_label?: string;
};

export function readQcDashboard(page: number) {
  return post<QcDashboard & { ok: true }>({ op: "qc-dashboard", page });
}

export function listQcHistoryPage(input: { status: QcHistoryStatus; page: number; sort: "newest" | "oldest" }) {
  return post<{ ok: true; sessions: QcHistorySession[]; total: number; page: number }>({
    op: input.status === "final" ? "list-final-qc" : "list-session-page",
    ...input,
  });
}

export function refailFinalQcItem(sessionId: string, itemId: string, note: string) {
  return post<{ ok: true; observation_id: string }>({
    op: "final-qc-refail",
    session_id: sessionId,
    item_id: itemId,
    note,
  });
}

export function markReadyForFinalCert(sessionId: string) {
  return post<{ ok: true }>({ op: "final-cert-ready", session_id: sessionId });
}

export function passFinalQc(sessionId: string, initials: string) {
  return post<{ ok: true }>({ op: "final-qc-pass", session_id: sessionId, initials });
}

export function startQcCorrection(sessionId: string, categoryId?: string) {
  return post<{ ok: true; session_id: string }>({
    op: "start-qc-correction",
    session_id: sessionId,
    category_id: categoryId,
  });
}

export function saveQcCorrection(sessionId: string, actions: { observation_id: string; action: string }[]) {
  return post<{ ok: true; lines: { observation_id: string; action: string }[] }>({
    op: "save-qc-correction",
    session_id: sessionId,
    actions,
  });
}

export function deleteQcReport(sessionId: string) {
  return post<{ ok: true }>({ op: "delete-qc-report", session_id: sessionId });
}

export function listQcCorrectionPhotos(sessionId: string) {
  return post<{ ok: true; photos: { observation_id: string; cdn_url: string; kind: "failure" | "fix" }[] }>({
    op: "qc-correction-photos",
    session_id: sessionId,
  });
}
